import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../api/client";
import { useEvent } from "../context/EventProvider";
import { sameSession } from "../auth/session";
import { useSessionKey } from "../auth/useSessionKey";
import { logAction } from "../log/actionLog";
import { applyPromoCode, type Resume } from "./articles";
import { useParticipation } from "./useParticipation";
import { useT } from "../settings/context";
import type { Say } from "../settings/say";
import { Button } from "../ui/Button";

/**
 * LE CODE PROMO, SAISI PAR LE PARTICIPANT (#362).
 *
 * L'organisateur crée ses codes au back office (`ArticleCodes`) ; jusqu'ici seul lui
 * pouvait en poser un, depuis la fiche. La V1 offrait un lien « J'ai un code promo »
 * sous son récapitulatif (`fo-meeting/src/screens/steps/resume/Code.jsx`) : c'est ce
 * modèle-ci, servi aux deux endroits où la V2 lit le solde — le récapitulatif de la
 * billetterie (« Ma commande » comprise) et le bloc de paiement de la confirmation.
 *
 * ⚠️ LE CODE S'ÉCRIT TOUT DE SUITE, en base, et pas avec le reste de l'inscription :
 * c'est la route de la V1 (`setCodeUser`), qui ne connaît pas la sélection en cours.
 * Le solde, lui, la connaît (`invoices/resume` lit le code posé et l'applique aux
 * articles qu'on lui passe) : périmer `articles-resume` suffit à faire apparaître la
 * remise sous le panier, avant même qu'il soit enregistré.
 *
 * ⚠️ UN CODE QUI A SERVI À UN PAIEMENT NE SE CHANGE PLUS : la V1 grisait son champ, on
 * le retire, et le serveur le refuse de toute façon (`CODE_IS_USED`). Le code reste
 * nommé, pour qu'on sache ce qui a été déduit.
 */
export function PromoCode({ resume }: { resume: Resume | undefined }) {
  const t = useT();
  const event = useEvent();
  const { me } = useParticipation();
  const sessionKey = useSessionKey();
  const client = useQueryClient();

  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [sending, setSending] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  if (!resume || !event || typeof me?.id !== "number") return null;
  const current = resume.promoCode;

  const apply = async () => {
    const code = typed.trim();
    if (code === "" || sending) return;
    // ⚠️ Session changée depuis le rendu (autre onglet) : ce qui est ici est à l'ancien compte, rien ne part.
    if (!sameSession(sessionKey)) return;
    setSending(true);
    setRefusal(null);
    try {
      await applyPromoCode(event.domainId, event.eventId, me.id, code);
      logAction(event, "promoCode", { label: code });
      /* ⚠️ LE CINQUIÈME ÉCRIVAIN DE LA COMMANDE (voir `useResume`) : sans cette
         péremption, le total resterait celui d'avant le code. Attendue, pour que le
         champ ne se referme pas sur un récapitulatif encore sans remise. */
      await client.invalidateQueries({ queryKey: ["articles-resume"] });
      setOpen(false);
    } catch (error) {
      setRefusal(refusalOf(error, t));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="promo">
      {current && (
        <p className="hint" role="status">
          {t("PROMO_CODE_APPLIED", "Code « $1 » : $2 % de remise sur « $3 »", [
            current.code,
            current.rate,
            current.article,
          ])}
        </p>
      )}

      {!current?.locked && !open && (
        <button
          type="button"
          className="lnk"
          onClick={() => {
            setTyped(current?.code ?? "");
            setRefusal(null);
            setOpen(true);
          }}
        >
          {t("PROMO_CODE_SHOW", "J'ai un code promo")}
        </button>
      )}

      {!current?.locked && open && (
        <form
          className="promo__f"
          onSubmit={(e) => {
            e.preventDefault();
            void apply();
          }}
        >
          <input
            type="text"
            /* Un code se recopie tel quel : ni correcteur, ni majuscule imposée, ni
               suggestion d'un code tapé ailleurs. */
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            /* Ouvert d'un clic pour y taper : le curseur y va tout de suite. */
            autoFocus
            aria-label={t("PROMO_CODE_PLACEHOLDER", "Saisir un code promo")}
            placeholder={t("PROMO_CODE_PLACEHOLDER", "Saisir un code promo")}
            value={typed}
            aria-invalid={refusal !== null}
            onChange={(e) => {
              setTyped(e.target.value);
              setRefusal(null);
            }}
          />
          <Button
            type="submit"
            loading={sending}
            disabled={typed.trim() === ""}
          >
            {t("PROMO_CODE_BUTTON", "Activer le code")}
          </Button>
        </form>
      )}

      {refusal && (
        <p className="loginerr" role="alert">
          {refusal}
        </p>
      )}
    </div>
  );
}

/**
 * LES REFUS DE LA ROUTE, DITS AVEC LES MOTS DE LA V1 (ils sont au dictionnaire depuis
 * elle). Le serveur les rend comme message d'une réponse 400.
 */
function refusalOf(error: unknown, t: Say): string {
  const message = error instanceof ApiError ? error.message : "";
  if (message === "CODE_NOT_FOUND") {
    return t("CODE_NOT_FOUND", "Le code que vous avez saisi n'existe pas");
  }
  if (message === "CODE_OUT_OF_STOCK") {
    return t(
      "CODE_OUT_OF_STOCK",
      "Ce code a déjà été utilisé au maximum de sa valeur remise, veuillez contacter l'organisateur.",
    );
  }
  if (message === "CODE_IS_USED") {
    return t("CODE_IS_USED", "Ce code a déjà été utilisé");
  }
  return t(
    "PROMO_CODE_NOT_APPLIED",
    "Le code n'a pas pu être appliqué. Réessayez dans un instant.",
  );
}
